// ============================================================================
// BOQ — library item activity, from raw audit rows to readable events
// ============================================================================
// boq.item_activity() returns one boq.audit_log row per changed row: the item,
// each version, each classification tag, each rate. A single save writes many
// of those in one transaction, so they share a timestamp and an author. This
// groups them into one event per save and describes each in a line a curator
// can read, without the columns nobody edits by hand.
//
// Pure, so the formatting is unit tested and the modal only renders.
// ============================================================================

const TAXONOMY_NAMES = {
  trade_group: 'Trade group',
  brio_trade: 'Chapter / sub-trade',
  trade_type: 'Trade type',
};

const VERSION_FIELDS = [
  ['short_description', 'Description', 'short'],
  ['unit', 'Unit', 'short'],
  ['measurement_ref', 'Measurement ref', 'short'],
  ['status', 'Status', 'short'],
  ['change_reason', 'Change reason', 'short'],
  ['long_spec_general', 'General spec', 'long'],
  ['long_spec_products', 'Products spec', 'long'],
  ['long_spec_execution', 'Execution spec', 'long'],
];

const RATE_FIELDS = [
  ['rate_quoted', 'Quoted', true],
  ['rate_billed', 'Billed', true],
  ['rate_date', 'Date'],
  ['contractor', 'Contractor'],
  ['source', 'Source'],
];

// Word-level diff of two texts, for the long spec fields. Returns segments
// { type: 'same' | 'del' | 'add', text } in reading order. Whitespace is kept
// as its own token, so joining the 'same' + 'del' segments gives back the old
// text exactly, and 'same' + 'add' the new one.
//
// The common head and tail are trimmed first, which covers the usual edit (a
// few words changed inside a long spec) cheaply. The middle is diffed by LCS
// only while it is small enough; past that it is shown as one removal and one
// addition rather than stalling the panel on a pathological pair.
const LCS_CELL_LIMIT = 250000;
export function diffWords(before, after) {
  const a = String(before ?? '').split(/(\s+)/).filter((t) => t !== '');
  const b = String(after ?? '').split(/(\s+)/).filter((t) => t !== '');
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head += 1;
  let tail = 0;
  while (tail < a.length - head && tail < b.length - head
    && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail += 1;
  const midA = a.slice(head, a.length - tail);
  const midB = b.slice(head, b.length - tail);

  const out = [];
  const push = (type, text) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  push('same', a.slice(0, head).join(''));

  if (midA.length * midB.length > LCS_CELL_LIMIT) {
    push('del', midA.join(''));
    push('add', midB.join(''));
  } else {
    // dp[i][j] = LCS length of midA[i..] and midB[j..]
    const n = midA.length; const m = midB.length;
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        dp[i][j] = midA[i] === midB[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    let i = 0; let j = 0;
    while (i < n && j < m) {
      if (midA[i] === midB[j]) { push('same', midA[i]); i += 1; j += 1; }
      else if (dp[i + 1][j] >= dp[i][j + 1]) { push('del', midA[i]); i += 1; }
      else { push('add', midB[j]); j += 1; }
    }
    while (i < n) { push('del', midA[i]); i += 1; }
    while (j < m) { push('add', midB[j]); j += 1; }
  }

  push('same', a.slice(a.length - tail).join(''));
  return out;
}

const shown = (v) => (v === null || v === undefined || v === '' ? 'empty' : String(v));
// Version status as the library shows it: 'deprecated' reads as archived.
const shownStatus = (v) => (v === 'deprecated' ? 'archived' : shown(v));

const money = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : String(v);
};

const rateSummary = (r) => {
  if (!r) return 'rate';
  const val = money(r.rate_billed) ?? money(r.rate_quoted);
  const kind = r.rate_billed != null ? 'billed' : 'quoted';
  return [
    val ? `${val} ${kind}` : 'no value',
    r.rate_date ? `dated ${r.rate_date}` : null,
    r.source ? `from ${r.source}` : null,
  ].filter(Boolean).join(', ');
};

// One row → zero or more lines. Tag rows are returned separately so a save
// that swaps a tag reads as "A → B" rather than as a removal and an addition.
function describeRow(row) {
  const { table, action, before, after, versionCode } = row;
  const b = before || {};
  const a = after || {};

  if (table === 'items') {
    if (action === 'INSERT') return [`Item created as ${a.item_code}`];
    if (action === 'DELETE') return [`Item ${b.item_code} deleted`];
    const lines = [];
    if (a.item_code !== b.item_code) lines.push({ text: `Code ${b.item_code} → ${a.item_code}`, kind: 'code' });
    if (a.status !== b.status) lines.push(`Item status ${shown(b.status)} → ${shown(a.status)}`);
    return lines;
  }

  if (table === 'item_versions') {
    const code = versionCode || a.full_code || b.full_code;
    if (action === 'INSERT') {
      return [`Version ${code} added${a.short_description ? `: ${a.short_description}` : ''}`];
    }
    if (action === 'DELETE') return [`Version ${b.full_code || code} removed`];
    // item_code / full_code changes come from a renumber cascading into every
    // version; the item row already says so once.
    return VERSION_FIELDS
      .filter(([f]) => (a[f] ?? null) !== (b[f] ?? null))
      .map(([f, label, size]) => (size === 'long'
        // Long specs are paragraphs: shown as the whole old and new text, one
        // under the other, with the words that changed marked in each.
        ? { text: `${code}: ${label}`, kind: 'spec', before: b[f] ?? '', after: a[f] ?? '', diff: diffWords(b[f], a[f]) }
        : `${code}: ${label} ${(f === 'status' ? shownStatus : shown)(b[f])} → ${(f === 'status' ? shownStatus : shown)(a[f])}`));
  }

  if (table === 'rates') {
    const code = versionCode ? ` on ${versionCode}` : '';
    if (action === 'INSERT') return [`Rate added${code}: ${rateSummary(a)}`];
    if (action === 'DELETE') return [`Rate removed${code}: ${rateSummary(b)}`];
    const lines = RATE_FIELDS
      .filter(([f]) => (a[f] ?? null) !== (b[f] ?? null))
      .map(([f, label, isMoney]) => {
        const fmt = (v) => shown(isMoney ? money(v) : v);
        return `Rate${code}: ${label} ${fmt(b[f])} → ${fmt(a[f])}`;
      });
    if (a.item_version_id !== b.item_version_id) lines.push(`Rate moved to version ${versionCode || 'another version'}`);
    return lines;
  }

  return [];
}

// ── Whole-event readings ────────────────────────────────────────────────────
// Some saves are one action that touches many rows; they read as that action,
// not as the rows. All are recognised from the rows alone.

// Creating an item writes the item, its first version and its tags in a few
// quick saves. That whole burst reads as one line, "Item X created": what was
// typed while creating is the starting point, not a change to it. A save counts
// as part of the burst when it only inserts, is by the same person, lands
// within this window of the item row, and adds versions to that item only.
const CREATE_WINDOW_MS = 2 * 60 * 1000;
const CREATE_TABLES = new Set(['items', 'item_versions', 'item_version_classifications']);

const versionUpdates = (ev) => ev.rows.filter((r) => r.table === 'item_versions' && r.action === 'UPDATE');

// The item row inserted on its own (an unmerge also inserts one, alongside the
// versions it moves back).
const createdCode = (ev) => {
  const ins = ev.rows.find((r) => r.table === 'items' && r.action === 'INSERT');
  if (!ins || versionUpdates(ev).length) return null;
  return ins.after?.item_code || null;
};

// boq.merge_library_items: the absorbed item row deleted, its versions moved.
const mergeOf = (ev) => {
  const del = ev.rows.find((r) => r.table === 'items' && r.action === 'DELETE');
  if (!del) return null;
  const from = del.before?.item_code;
  const moved = versionUpdates(ev).filter((r) => r.before?.item_code === from && r.after?.item_code && r.after.item_code !== from);
  return moved.length ? { from, into: moved[0].after.item_code } : null;
};

// boq.unmerge_library_items: an item row put back, versions moved onto it.
const unmergeOf = (ev) => {
  const ins = ev.rows.find((r) => r.table === 'items' && r.action === 'INSERT');
  if (!ins) return null;
  const to = ins.after?.item_code;
  const moved = versionUpdates(ev).filter((r) => r.after?.item_code === to && r.before?.item_code && r.before.item_code !== to);
  return moved.length ? { to, from: moved[0].before.item_code, count: moved.length } : null;
};

// The Active button: version status changes and nothing else, one of them to
// active. Reads as "X set as active"; the versions it archived are implied.
const IGNORED_VERSION_COLUMNS = new Set(['updated_at', 'updated_by']);
const setActiveOf = (ev) => {
  if (ev.rows.some((r) => r.table !== 'item_versions' || r.action !== 'UPDATE')) return null;
  const onlyStatus = ev.rows.every((r) => Object.keys({ ...r.before, ...r.after })
    .every((k) => k === 'status' || IGNORED_VERSION_COLUMNS.has(k) || (r.before?.[k] ?? null) === (r.after?.[k] ?? null)));
  if (!onlyStatus) return null;
  const up = ev.rows.filter((r) => r.after?.status === 'active' && r.before?.status !== 'active');
  return up.length === 1 ? (up[0].versionCode || up[0].after.full_code) : null;
};

/**
 * Group audit rows into events, newest first.
 *
 * @param rows [{ id, changedAt, table, action, actorName, before, after,
 *               versionCode, nodeLabel, taxonomyCode }]
 * @returns [{ key, changedAt, actorName, lines: [{ text, kind?, before?, after? }] }]
 *          Events with nothing left to say (a renumber's per-version echo, a
 *          save that changed only bookkeeping columns) are dropped.
 */
export function buildItemActivity(rows = []) {
  const events = [];
  const byKey = new Map();
  rows.forEach((r) => {
    const key = `${r.changedAt}|${r.actorName || ''}`;
    if (!byKey.has(key)) {
      const ev = { key, changedAt: r.changedAt, actorName: r.actorName || null, rows: [] };
      byKey.set(key, ev);
      events.push(ev);
    }
    byKey.get(key).rows.push(r);
  });

  // Oldest first, to fold creation bursts and to know which version was in
  // use when a merge happened (replayed from the version rows themselves).
  // ISO timestamps from one source sort as text.
  const chrono = [...events].sort((x, y) => String(x.changedAt).localeCompare(String(y.changedAt)));
  const versionState = new Map();          // version id → { itemCode, fullCode, status }
  let burst = null;                        // { code, actor, at }
  chrono.forEach((ev) => {
    const created = createdCode(ev);
    const t = Date.parse(ev.changedAt);
    if (created) {
      burst = { code: created, actor: ev.actorName, at: t };
      ev.headline = [{ text: `Item ${created} created` }];
    } else if (burst && ev.actorName === burst.actor && t - burst.at <= CREATE_WINDOW_MS
      && ev.rows.every((r) => r.action === 'INSERT' && CREATE_TABLES.has(r.table)
        && (r.table !== 'item_versions' || r.after?.item_code === burst.code))) {
      ev.folded = true;
    } else {
      burst = null;
    }

    const merge = mergeOf(ev);
    const unmerge = !merge && unmergeOf(ev);
    if (merge) {
      const inUse = [...versionState.values()]
        .filter((v) => v.itemCode === merge.into && v.status === 'active')
        .map((v) => v.fullCode);
      ev.headline = [
        { text: `${merge.from} merged into ${merge.into}`, kind: 'code' },
        { text: inUse.length ? `${inUse.join(', ')} set as active` : `${merge.into} kept as the active item` },
      ];
    } else if (unmerge) {
      ev.headline = [
        { text: `${unmerge.to} unmerged from ${unmerge.from}`, kind: 'code' },
        { text: `${unmerge.count} version${unmerge.count === 1 ? '' : 's'} returned to ${unmerge.to}` },
      ];
    } else if (!created) {
      const active = setActiveOf(ev);
      if (active) ev.headline = [{ text: `${active} set as active` }];
    }

    ev.rows.forEach((r) => {
      if (r.table !== 'item_versions') return;
      const id = r.after?.id || r.before?.id;
      if (!id) return;
      if (r.action === 'DELETE') { versionState.delete(id); return; }
      versionState.set(id, { itemCode: r.after.item_code, fullCode: r.after.full_code, status: r.after.status });
    });
  });

  return events
    .filter((ev) => !ev.folded)
    .map((ev) => {
      if (ev.headline) return { key: ev.key, changedAt: ev.changedAt, actorName: ev.actorName, lines: ev.headline };
      const lines = [];
      // Tags: per taxonomy, what was removed and what was added in this save.
      // A tag sits on every version, so the same label repeats; sets dedupe it.
      const tags = new Map();
      ev.rows.forEach((r) => {
        if (r.table !== 'item_version_classifications') {
          describeRow(r).forEach((l) => lines.push(typeof l === 'string' ? { text: l } : l));
          return;
        }
        const tax = r.taxonomyCode || 'other';
        if (!tags.has(tax)) tags.set(tax, { removed: new Set(), added: new Set() });
        const label = r.nodeLabel || 'a node since deleted';
        if (r.action === 'INSERT') tags.get(tax).added.add(label);
        if (r.action === 'DELETE') tags.get(tax).removed.add(label);
      });
      tags.forEach(({ removed, added }, tax) => {
        const name = TAXONOMY_NAMES[tax] || tax.replace(/_/g, ' ');
        // A label both removed and added in one save did not change.
        const gone = [...removed].filter((l) => !added.has(l));
        const came = [...added].filter((l) => !removed.has(l));
        if (!gone.length && !came.length) return;
        if (gone.length && came.length) lines.push({ text: `${name} ${gone.join(', ')} → ${came.join(', ')}`, kind: 'class' });
        else if (came.length) lines.push({ text: `${name} set to ${came.join(', ')}`, kind: 'class' });
        else lines.push({ text: `${name} ${gone.join(', ')} removed`, kind: 'class' });
      });
      // Code change first: it is the headline of a reclassification.
      lines.sort((x, y) => (y.kind === 'code') - (x.kind === 'code'));
      return { key: ev.key, changedAt: ev.changedAt, actorName: ev.actorName, lines };
    })
    .filter((ev) => ev.lines.length > 0);
}

export const formatActivityTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
